import { useCallback } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { isEqual } from 'lodash';
import { Card, Stack } from '@mui/material';

import { FormLayout } from '@occc-app/routes/CampaignComponents/components/FormLayout';
import { CampaignComponentFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentFooterActions';
import { CampaignComponentReadOnlyFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentReadOnlyFooterActions';
import { CanEditFormFields } from '@occc-app/App/CanEditFormFieldsContext';

import { Flow_Types } from '@occc-app/offerHelpers/types';
import {
  InAudienceConstraint,
  LocationInStateConstraint,
  StoreLocationOrGroupsConstraint,
  SubBrandConstraint,
} from '@occc/feat-constraints';
import { TargetFormProps as TargetFormParams, TargetFormValues } from './types';
import { customSectionFormMutators } from './CustomSection';

import { OfferForm } from '../components/OfferForm';

type TargetFormProps = TargetFormParams & { onNextStepButtonClick: () => void };

export const TargetForm = ({
  onSubmit,
  initialValues,
  chainId,
  onCancelClick,
  onNextStepButtonClick,
  loading,
  shouldShowStateRestrictions = true,
  shouldShowStoreLocationOrGroups = true,
  shouldShowSubBrands = false,
  currentFlowType,
  isFormEditable,
  customSection,
  offerId,
  onRefetchOffer,
  isRefetching,
}: TargetFormProps) => {
  const renderForm: FormRenderProps<TargetFormValues>['render'] = useCallback(
    ({ handleSubmit, submitting, values, dirty }: FormRenderProps<TargetFormValues>) => {
      const handleCancelClick = () => {
        onCancelClick(dirty);
      };

      const formFooterActionButtons = isFormEditable ? (
        <CampaignComponentFooterActions
          currentFlowType={currentFlowType}
          onCancel={handleCancelClick}
          isSubmitDisabled={submitting || (currentFlowType === Flow_Types.Update && !dirty)}
        />
      ) : (
        <CampaignComponentReadOnlyFooterActions
          onCancel={handleCancelClick}
          onNextStepClick={onNextStepButtonClick}
        />
      );

      return (
        <OfferForm dirty={!submitting && dirty} handleSubmit={handleSubmit}>
          <FormLayout actions={formFooterActionButtons} type='primary' isLoading={loading}>
            {customSection?.render ? (
              <Card sx={{ mb: 1 }}>
                <Stack p={3} spacing={3}>
                  {customSection?.render({
                    values,
                    isFormEditable,
                    currentFlowType,
                    offerId,
                    onRefetchOffer,
                    isRefetching,
                  })}
                </Stack>
              </Card>
            ) : null}
            <Card>
              <Stack p={3} spacing={3}>
                <InAudienceConstraint values={values} chainId={chainId} />
                {shouldShowStateRestrictions && <LocationInStateConstraint />}
                {shouldShowStoreLocationOrGroups && (
                  <StoreLocationOrGroupsConstraint type={values.storeLocationOrGroups.type} />
                )}
                {shouldShowSubBrands && <SubBrandConstraint />}
              </Stack>
            </Card>
          </FormLayout>
        </OfferForm>
      );
    },
    [
      isFormEditable,
      currentFlowType,
      onNextStepButtonClick,
      chainId,
      shouldShowStateRestrictions,
      shouldShowStoreLocationOrGroups,
      onCancelClick,
      shouldShowSubBrands,
      customSection,
      offerId,
      onRefetchOffer,
      isRefetching,
      loading,
    ],
  );
  return (
    <CanEditFormFields isEditable={isFormEditable}>
      <Form<TargetFormValues>
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={customSection?.getValidation}
        render={renderForm}
        initialValuesEqual={isEqual}
        keepDirtyOnReinitialize
        mutators={customSection ? customSectionFormMutators : undefined}
      />
    </CanEditFormFields>
  );
};
